import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { TABLE_ALIGN, TABLE_ALIGNS, TABLE_SIZE, TABLE_SIZES } from './constants';
import {
    TABLE_COLUMN_PROP_TYPES,
    getColumnLabel,
    getColumnStyle,
    normalizeAlign,
    normalizeColumns,
    tableColumnPropType,
} from './columns';

const Cell = ({ children, align = TABLE_ALIGN.LEFT, header = false, className, style, colSpan, ...rest }) => {
    const Tag = header ? 'th' : 'td';
    return (
        <Tag
            className={classNames('ds-table__cell', `ds-table__cell--${normalizeAlign(align)}`, { 'ds-table__cell--header': header }, className)}
            scope={header ? 'col' : undefined}
            style={style}
            colSpan={colSpan}
            {...rest}
        >
            {children}
        </Tag>
    );
};

Cell.propTypes = {
    /** Cell content */
    children: PropTypes.node,
    /** Horizontal alignment */
    align: PropTypes.oneOf(TABLE_ALIGNS),
    /** Render as a header cell */
    header: PropTypes.bool,
    /** Extra class */
    className: PropTypes.string,
    /** Inline style (column widths come from `columns`) */
    style: PropTypes.object,
    /** colSpan passthrough */
    colSpan: PropTypes.number,
};

const Row = ({ children, className, disabled = false, ...rest }) => (
    <tr className={classNames('ds-table__row', { 'ds-table__row--disabled': disabled }, className)} aria-disabled={disabled || undefined} {...rest}>
        {children}
    </tr>
);

Row.propTypes = {
    /** Cells */
    children: PropTypes.node,
    /** Extra class */
    className: PropTypes.string,
    /** Mute the row (evaluated, locked) */
    disabled: PropTypes.bool,
};

/**
 * Table primitive: a header row built from `columns`, body rows passed as children
 * (`Table.Row` / `Table.Cell`), horizontal scroll on narrow screens and an empty state when
 * there are no rows. Column widths and alignment are declared once in `columns` and applied
 * to the header; body cells use `Table.Cell align`. For data-bound rows with sorting, selection
 * and paging use DataTable, which takes the same column shape.
 */
const Table = ({
    columns = [],
    children,
    emptyText,
    hoverable = true,
    size = TABLE_SIZE.MD,
    caption,
    className,
    ...rest
}) => {
    const cols = normalizeColumns(columns);
    const rows = React.Children.toArray(children).filter(Boolean);

    return (
        <div
            className={classNames(
                'ds-table',
                `ds-table--${TABLE_SIZES.includes(size) ? size : TABLE_SIZE.MD}`,
                { 'ds-table--hoverable': hoverable },
                className,
            )}
            {...rest}
        >
            <table className="ds-table__table">
                {caption && <caption className="ds-table__caption">{caption}</caption>}
                {cols.length > 0 && (
                    <thead className="ds-table__head">
                        <tr className="ds-table__row ds-table__row--head">
                            {cols.map((column) => (
                                <Cell
                                    key={column.id}
                                    header
                                    align={column.align}
                                    style={getColumnStyle({ ...column, minWidth: column.minWidth ?? column.width })}
                                    className={column.headerClassName}
                                >
                                    {column.srOnly ? <span className="ds-table__sr-only">{column.header}</span> : column.header}
                                </Cell>
                            ))}
                        </tr>
                    </thead>
                )}
                <tbody className="ds-table__body">
                    {rows.length > 0 ? rows : (
                        emptyText != null && (
                            <tr className="ds-table__row ds-table__row--empty">
                                <td className="ds-table__cell ds-table__cell--empty" colSpan={Math.max(cols.length, 1)}>
                                    {emptyText}
                                </td>
                            </tr>
                        )
                    )}
                </tbody>
            </table>
        </div>
    );
};

Table.propTypes = {
    /** Header columns — the shape DataTable shares: `{ id, header, label, width, align, srOnly, … }`,
     *  or a header string. A `width` also pins the column's minimum width. */
    columns: PropTypes.arrayOf(tableColumnPropType),
    /** Body rows (`Table.Row`) */
    children: PropTypes.node,
    /** Shown in a single full-width cell when there are no rows */
    emptyText: PropTypes.node,
    /** Tint body rows on hover */
    hoverable: PropTypes.bool,
    /** Row density */
    size: PropTypes.oneOf(TABLE_SIZES),
    /** Visually hidden caption for screen readers */
    caption: PropTypes.node,
    /** Extra class on the scroll wrapper */
    className: PropTypes.string,
};

Table.Row = Row;
Table.Cell = Cell;

export {
    Row as TableRow,
    Cell as TableCell,
    TABLE_ALIGN,
    TABLE_ALIGNS,
    TABLE_COLUMN_PROP_TYPES,
    TABLE_SIZE,
    TABLE_SIZES,
    getColumnLabel,
    getColumnStyle,
    normalizeColumns,
    tableColumnPropType,
};
export default Table;
